import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Flex, Grid, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';

import { fg } from '@confluence/feature-gating';

import { HTV2NthUserLearningOpportunityCard } from './HTV2NthUserLearningOpportunityCard.tsx';
import { useHTV2Actions } from './useHTV2Store.ts';

const gridStyles = cssMap({
	root: {
		gridTemplateColumns: 'repeat(calc(3 - var(--homeIsSingleColumn, 0)), 1fr)',
	},
});

export const LEARNING_PATH_LINK =
	'https://university.atlassian.com/student/collection/850385/path/1083904?utm_source=in-product-banner&utm_medium=inapp&utm_campaign=P:uni-ondemand*O:university*W:confluence*H:fy25-q1*I:in-product-onboarding-confluence*';

export const SUPPORT_RESOURCES_LINK = 'https://support.atlassian.com/confluence-cloud/';

export const JOIN_COMMUNITY_LINK =
	'https://community.atlassian.com/forums/New-to-Confluence/gh-p/newtoconfluence';

const i18n = defineMessages({
	learningPathTitle: {
		id: 'experiment-home-onboarding-takeover-v2.learning-path.title',
		defaultMessage: 'Free learning path on Atlassian University',
		description:
			'This will be shown as a title on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	learningPathDescription: {
		id: 'experiment-home-onboarding-takeover-v2.learning-path.description',
		defaultMessage: 'Follow short, self-paced courses to get up and running in Confluence quickly.',
		description:
			'This will be shown as a description on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	learningPathCta: {
		id: 'experiment-home-onboarding-takeover-v2.learning-path.cta',
		defaultMessage: 'Get started',
		description:
			'This will be shown as a cta button on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	supportResourcesTitle: {
		id: 'experiment-home-onboarding-takeover-v2.support-resources.title',
		defaultMessage: 'Discover Atlassian Support resources',
		description:
			'This will be shown as a title on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	supportResourcesDescription: {
		id: 'experiment-home-onboarding-takeover-v2.support-resources.description',
		defaultMessage: 'Browse articles on all things Confluence on the Atlassian Support site.',
		description:
			'This will be shown as a description on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	supportResourcesCta: {
		id: 'experiment-home-onboarding-takeover-v2.support-resources.cta',
		defaultMessage: 'Learn more',
		description:
			'This will be shown as a cta button on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	joinCommunityTitle: {
		id: 'experiment-home-onboarding-takeover-v2.join-community.title',
		defaultMessage: 'Join users like you on Atlassian Community',
		description:
			'This will be shown as a title on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	joinCommunityDescription: {
		id: 'experiment-home-onboarding-takeover-v2.join-community.description',
		defaultMessage:
			'Learn from new users like you or share your wisdom with others in the community.',
		description:
			'This will be shown as a description on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	joinCommunityCta: {
		id: 'experiment-home-onboarding-takeover-v2.join-community.cta',
		defaultMessage: 'Join Community',
		description:
			'This will be shown as a cta button on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	heading: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-learning-opportunity.heading',
		defaultMessage: 'Learn with more resources',
		description:
			'This will be shown as the section heading above a few cards that represent learning opportunities.',
	},
	dismissLearningResources: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-learning-opportunity.dismiss-learning-resources',
		defaultMessage: 'Dismiss learning resources',
		description:
			'The text displayed when hovering over the dismiss learning resources button above a list of learning opportunities.',
	},
});

const HTV2NthUserLearningOpportunityCards = [
	{
		title: i18n.learningPathTitle,
		description: i18n.learningPathDescription,
		cta: i18n.learningPathCta,
		link: LEARNING_PATH_LINK,
	},
	{
		title: i18n.joinCommunityTitle,
		description: i18n.joinCommunityDescription,
		cta: i18n.joinCommunityCta,
		link: JOIN_COMMUNITY_LINK,
	},
	{
		title: i18n.supportResourcesTitle,
		description: i18n.supportResourcesDescription,
		cta: i18n.supportResourcesCta,
		link: SUPPORT_RESOURCES_LINK,
	},
];

type HTV2NthUserLearningCardsListProps = {
	onboardingPeriodDay: number;
	onDismissSection?: () => void;
};

export const HTV2NthUserLearningOpportunityCardsList = ({
	onboardingPeriodDay,
	onDismissSection,
}: HTV2NthUserLearningCardsListProps) => {
	const { formatMessage } = useIntl();
	const { getIsXflowTakeoverType } = useHTV2Actions();
	const isXflowTakeoverType = getIsXflowTakeoverType();

	return (
		<Stack testId="htv2-learning-resources" space="space.150">
			<Flex justifyContent="space-between" alignItems="center">
				<Heading size="xsmall">
					<FormattedMessage {...i18n.heading} />
				</Heading>
				{onDismissSection && fg('cc_onboarding_takeover_dismiss_button') && (
					<IconButton
						testId="htv2-learning-resources-dismiss-button"
						onClick={onDismissSection}
						label={formatMessage(i18n.dismissLearningResources)}
						spacing={isXflowTakeoverType ? 'default' : 'compact'}
						appearance={isXflowTakeoverType ? 'default' : 'subtle'}
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
					/>
				)}
			</Flex>
			<Grid gap="space.200" xcss={gridStyles.root}>
				{HTV2NthUserLearningOpportunityCards.map((learningOpportunityCardContent) => {
					return (
						<HTV2NthUserLearningOpportunityCard
							key={`${formatMessage(learningOpportunityCardContent.title)}`}
							{...learningOpportunityCardContent}
							onboardingPeriodDay={onboardingPeriodDay}
						/>
					);
				})}
			</Grid>
		</Stack>
	);
};
